import configData from '../tenants/adaptia/config.json';
import type { BrandConfig } from '@toki/theme';

/**
 * Shape of a tenant's configuration file. Loaded once at build time.
 * Mirror del que vive en apps/shell/src/tenant.ts. Cuando llegue el
 * segundo cliente, ambos se moveran a un paquete compartido o pasaran
 * a cargarse en runtime desde el backend.
 */
export interface TenantConfig {
  displayName: string;
  displayNameLong?: string;
  /** Marca cuadrada para el raíl COLAPSADO. El `brand.logo` es el wordmark
   *  horizontal, que a 64px de ancho queda ilegible. */
  logoMark: string;
  /** Las mismas dos marcas, en su versión para SUPERFICIE OSCURA.
   *
   *  ⚠️ No es un extra: el raíl es oscuro (`--rail-bg: #001D33`) y una marca
   *  pensada para fondo claro ahí no se degrada, desaparece — el wordmark de
   *  Adaptia en tinta sobre el raíl es invisible y su símbolo en degradado
   *  violeta→rosa se apaga. Y el raíl ya ha cambiado de superficie TRES veces
   *  (azul → blanco → oscuro), así que las dos variantes tienen que existir
   *  las dos, no la que toque hoy.
   *
   *  Si un tenant no las declara se cae a las claras: se verá mal, pero se
   *  verá algo, que es mejor que un hueco. */
  logoOnDark: string;
  logoMarkOnDark: string;
  /** Cómo se LEE la marca, que no tiene por qué ser el `displayName`. Aquí el
   *  producto se llama «Kivai» y la marca del raíl es «Adaptia»: un `alt` que
   *  dijera «Kivai» describiría una imagen que no es. */
  logoAlt: string;
  brand: BrandConfig;
  backend: {
    baseUrl: string;
  };
}

// Vite con `base: '/admin/'` sirve los assets de public/ en `/admin/...`
// en prod pero en `/...` en dev. BASE_URL devuelve el prefijo correcto
// segun el entorno, asi que el JSON guarda solo la ruta relativa
// ('branding/logo.png') y aqui prependemos.
const asset = (path: string) => import.meta.env.BASE_URL + path;

// `brand` del JSON lleva más campos de los que declara `BrandConfig` (el tipo
// vive en @toki/theme, que es de Toki y no se toca desde aquí). Se leen del
// dato importado, que sí los tiene.
const b = configData.brand as typeof configData.brand & {
  logoOnDark?: string;
  logoMarkOnDark?: string;
  logoAlt?: string;
};

export const tenantConfig: TenantConfig = {
  ...configData,
  logoMark: asset(b.logoMark),
  logoOnDark: asset(b.logoOnDark ?? b.logo),
  logoMarkOnDark: asset(b.logoMarkOnDark ?? b.logoMark),
  logoAlt: b.logoAlt ?? configData.displayName,
  brand: {
    ...configData.brand,
    logo: asset(b.logo),
  },
};
